import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RoleTemplates = ({ onApplyTemplate }) => {
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const roleTemplates = [
    {
      id: 'owner',
      name: 'Owner/Executive',
      description: 'Full system access with fleet-wide oversight and risk management capabilities',
      icon: 'Crown',
      color: 'bg-primary',
      permissions: [
        'Fleet Dashboard Access',
        'All Voyage Planning Features',
        'Complete Emissions Calculator',
        'Full EUA Trading Access',
        'All Compliance Reports',
        'Vessel Performance Analytics',
        'User Management',
        'System Administration',
        'API Access',
        'Data Export'
      ],
      userCount: 3
    },
    {
      id: 'chartering',
      name: 'Chartering Desk',
      description: 'Voyage planning, route optimization, and emissions calculation for charter operations',
      icon: 'Route',
      color: 'bg-accent',
      permissions: [
        'Fleet Dashboard View',
        'Voyage Planning & Optimization',
        'Route Optimization Tools',
        'Emissions Calculator',
        'Voyage Performance Reports',
        'Charter Party Integration',
        'Speed Optimization',
        'Weather Routing'
      ],
      userCount: 12
    },
    {
      id: 'finance',
      name: 'Finance Team',
      description: 'EUA trading, financial analysis, and carbon cost accounting capabilities',
      icon: 'DollarSign',
      color: 'bg-success',
      permissions: [
        'Fleet Dashboard View',
        'EUA Trading Hub',
        'Financial Reports',
        'Cost Allocation Tools',
        'EUA Forecasting',
        'Risk Management',
        'Hedge Position Tracking',
        'Cash Flow Analysis'
      ],
      userCount: 8
    },
    {
      id: 'compliance',
      name: 'Compliance Officer',
      description: 'Regulatory oversight, audit trails, and compliance report generation',
      icon: 'Shield',
      color: 'bg-warning',
      permissions: [
        'Fleet Dashboard View',
        'Compliance Reports',
        'Audit Trail Access',
        'Regulatory Submissions',
        'D/E Rating Monitoring',
        'CII Tracking',
        'FuelEU Compliance',
        'Document Management'
      ],
      userCount: 5
    },
    {
      id: 'operations',
      name: 'Operations Manager',
      description: 'Operational oversight with vessel management and performance monitoring',
      icon: 'Settings',
      color: 'bg-secondary',
      permissions: [
        'Fleet Dashboard View',
        'Vessel Management',
        'Performance Monitoring',
        'Operational Reports',
        'Fuel Consumption Tracking',
        'Route Monitoring',
        'Efficiency Analytics',
        'Alert Management'
      ],
      userCount: 15
    },
    {
      id: 'technical',
      name: 'Technical Superintendent',
      description: 'Technical vessel management with emissions monitoring and performance analysis',
      icon: 'Wrench',
      color: 'bg-accent',
      permissions: [
        'Fleet Dashboard View',
        'Vessel Technical Data',
        'Emissions Monitoring',
        'Performance Analytics',
        'Maintenance Integration',
        'Technical Reports',
        'Fuel Efficiency Tools',
        'IoT Sensor Data'
      ],
      userCount: 10
    }
  ];

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template?.id === selectedTemplate ? null : template?.id);
  };

  const handleApplyTemplate = (template) => {
    onApplyTemplate(template);
    setSelectedTemplate(null);
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-maritime-sm">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-foreground">Role Templates</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Pre-configured permission sets for common maritime roles
            </p>
          </div>
          <Button variant="outline" size="sm">
            <Icon name="Plus" size={14} strokeWidth={2} className="mr-2" />
            Create Template
          </Button>
        </div>
      </div>
      <div className="p-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          {roleTemplates?.map((template) => (
            <div
              key={template?.id}
              className={`relative p-4 rounded-lg border transition-maritime cursor-pointer ${
                selectedTemplate === template?.id
                  ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50 hover:bg-muted/30'
              }`}
              onClick={() => handleTemplateSelect(template)}
            >
              <div className="flex items-start space-x-3">
                <div className={`w-10 h-10 rounded-lg ${template?.color} flex items-center justify-center`}>
                  <Icon name={template?.icon} size={20} color="white" strokeWidth={2} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-foreground">{template?.name}</h4>
                    <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded">
                      {template?.userCount} users
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                    {template?.description}
                  </p>
                </div>
              </div>

              {selectedTemplate === template?.id && (
                <div className="mt-4 pt-4 border-t border-border">
                  <div className="space-y-2 mb-4">
                    <h5 className="text-xs font-medium text-foreground">Included Permissions:</h5>
                    <div className="max-h-32 overflow-y-auto">
                      {template?.permissions?.map((permission, index) => (
                        <div key={index} className="flex items-center space-x-2 text-xs text-muted-foreground">
                          <Icon name="Check" size={12} strokeWidth={2} className="text-success" />
                          <span>{permission}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Button
                      size="sm"
                      onClick={(e) => {
                        e?.stopPropagation();
                        handleApplyTemplate(template);
                      }}
                      className="flex-1"
                    >
                      Apply Template
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => e?.stopPropagation()}
                    >
                      <Icon name="Edit" size={14} strokeWidth={2} />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default RoleTemplates;